import type { BankIncomingAdminLine } from '@/types/bank-reconciliation';

export interface ConversionFromBankLinesFormState {
  soldLineId: string;
  boughtLineId: string;
}

export interface ConversionFromBankLinesErrors {
  soldLineId?: string;
  boughtLineId?: string;
  fundingHops?: string;
}

const currencyOf = (line: BankIncomingAdminLine) =>
  (line.currency ?? '').toUpperCase();

export function fundingHopsOf(
  lines: BankIncomingAdminLine[],
  state: ConversionFromBankLinesFormState
): BankIncomingAdminLine[] {
  return lines.filter(
    (line) => line.id !== state.soldLineId && line.id !== state.boughtLineId
  );
}

// Thousandths: the finest minor unit ISO 4217 uses, so no currency rounds.
const MINOR_UNITS_PER_MAJOR = 1000;

/** What the hops leave behind per currency; empty when they net out. */
export function unbalancedHopTotals(
  hops: BankIncomingAdminLine[]
): Map<string, number> {
  const net = new Map<string, number>();
  for (const hop of hops) {
    const currency = currencyOf(hop);
    const minorUnits = Math.round(hop.amount * MINOR_UNITS_PER_MAJOR);
    net.set(currency, (net.get(currency) ?? 0) + minorUnits);
  }
  return new Map(
    [...net]
      .filter(([, minorUnits]) => minorUnits !== 0)
      .map(([currency, minorUnits]) => [
        currency,
        minorUnits / MINOR_UNITS_PER_MAJOR,
      ])
  );
}

/** Money out and money in, in two currencies: worth offering to record. */
export function couldBeConversion(lines: BankIncomingAdminLine[]): boolean {
  return (
    lines.some((line) => line.amount < 0) &&
    lines.some((line) => line.amount > 0) &&
    new Set(lines.map(currencyOf)).size > 1
  );
}

/**
 * Mirrors the backend's checks, so a pick it would refuse is caught before
 * the round trip. Hops move the fund's own money, so they net out per currency.
 */
export function validateConversionFromBankLines(
  state: ConversionFromBankLinesFormState,
  lines: BankIncomingAdminLine[]
): ConversionFromBankLinesErrors {
  const errors: ConversionFromBankLinesErrors = {};
  const sold = lines.find((line) => line.id === state.soldLineId);
  const bought = lines.find((line) => line.id === state.boughtLineId);

  if (!sold) errors.soldLineId = 'Required';
  else if (sold.amount >= 0) errors.soldLineId = 'Must be money going out';
  else if (!currencyOf(sold)) errors.soldLineId = 'Line has no currency';
  else if (!sold.value_date) errors.soldLineId = 'Line has no value date';

  if (!bought) errors.boughtLineId = 'Required';
  else if (bought.amount <= 0) errors.boughtLineId = 'Must be money coming in';
  else if (!currencyOf(bought)) errors.boughtLineId = 'Line has no currency';
  else if (sold && currencyOf(sold) === currencyOf(bought))
    errors.boughtLineId = 'Must differ from the currency sold';
  else if (!bought.value_date) errors.boughtLineId = 'Line has no value date';
  else if (sold?.value_date && sold.value_date !== bought.value_date)
    errors.boughtLineId = 'Must settle on the date the sold line did';

  if (!sold || !bought) return errors;

  const hops = fundingHopsOf(lines, state);
  const tradeCurrencies = [currencyOf(sold), currencyOf(bought)];
  const stray = hops.find((hop) => !tradeCurrencies.includes(currencyOf(hop)));
  if (stray) {
    errors.fundingHops = `A ${currencyOf(stray) || 'currency-less'} line cannot fund a ${tradeCurrencies.join(' to ')} trade`;
  } else if (unbalancedHopTotals(hops).size > 0) {
    errors.fundingHops =
      'Funding hops must net to zero per currency - a line is missing, or one that is not a hop is selected';
  }

  return errors;
}

export function hasAnyError(errors: ConversionFromBankLinesErrors): boolean {
  return Object.values(errors).some((error) => error !== undefined);
}

/**
 * The sold/bought pairing to prefill, if the selection settles on one. With
 * hops selected the euro lines are ambiguous on their own; only the real legs
 * leave the rest netting out. Where two lines of one amount still tie, the
 * pair the bank itself typed as FX trades wins.
 */
export function likeliestPairing(
  lines: BankIncomingAdminLine[]
): ConversionFromBankLinesFormState | null {
  const valid: [BankIncomingAdminLine, BankIncomingAdminLine][] = [];
  for (const sold of lines) {
    if (sold.amount >= 0) continue;
    for (const bought of lines) {
      if (bought.amount <= 0) continue;
      const state = { soldLineId: sold.id, boughtLineId: bought.id };
      if (!hasAnyError(validateConversionFromBankLines(state, lines))) {
        valid.push([sold, bought]);
      }
    }
  }
  const tradedByBank = valid.filter(([sold, bought]) =>
    [sold, bought].every((leg) => leg.bank_transaction_type === 'FX_TRADE')
  );
  const pairing = pickOnly(valid) ?? pickOnly(tradedByBank);
  return pairing
    ? { soldLineId: pairing[0].id, boughtLineId: pairing[1].id }
    : null;
}

const pickOnly = <T>(candidates: T[]): T | undefined =>
  candidates.length === 1 ? candidates[0] : undefined;
